import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import Handler from "../../../Helpers/Handler";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";
import "../../../Assets/css/style.css";

const STAR_COUNT = 5;

const StarRating = ({ rating }) => (
  <div className="tv-stars">
    {Array.from({ length: STAR_COUNT }, (_, i) => {
      const filled = i + 1 <= Math.round(rating);
      return (
        <i
          key={i}
          className={`bx ${filled ? "bxs-star" : "bx-star"} tv-star ${filled ? "tv-star--filled" : ""}`}
        />
      );
    })}
    <span className="tv-rating-num">{rating > 0 ? Number(rating).toFixed(1) : "—"}</span>
  </div>
);



const VendorCard = ({ vendor, rank }) => {
  const initials = vendor.name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const totalCompleted =
    parseInt(vendor.completed_deals || 0) +
    parseInt(vendor.completed_events || 0);

  const score = (parseFloat(vendor.avg_rating) * 0.6 + totalCompleted * 0.4).toFixed(1);
  const barWidth = Math.min(100, (parseFloat(vendor.avg_rating) / 5) * 100);

  return (
    <div className={`tv-card ${rank <= 3 ? "tv-card--top" : ""}`}>
      

      <div className="tv-avatar-wrap">
        <div className="tv-avatar">{initials}</div>
        {parseFloat(vendor.avg_rating) >= 4.5 && (
          <span className="tv-verified-badge" title="Top Rated">
            <i className="bx bxs-badge-check" />
          </span>
        )}
      </div>

      <div className="tv-card-info">
        <h3 className="tv-vendor-name">{vendor.name}</h3>
        {vendor.mobile && (
          <div className="tv-vendor-meta">
            <i className="bx bx-phone" /> {vendor.mobile}
          </div>
        )}
      </div>

      <StarRating rating={parseFloat(vendor.avg_rating)} />

      <div className="tv-stats-row">
        <div className="tv-stat">
          <div className="tv-stat-value">{vendor.total_reviews}</div>
          <div className="tv-stat-label">Reviews</div>
        </div>
        <div className="tv-stat-divider" />
        <div className="tv-stat">
          <div className="tv-stat-value">{vendor.completed_deals}</div>
          <div className="tv-stat-label">Deals</div>
        </div>
        <div className="tv-stat-divider" />
        <div className="tv-stat">
          <div className="tv-stat-value">{vendor.completed_events}</div>
          <div className="tv-stat-label">Events</div>
        </div>
      </div>

      <div className="tv-score-bar-wrap">
        <div className="tv-score-bar-label">
          <span>Score</span>
          <span>{score}</span>
        </div>
        <div className="tv-score-bar">
          <div className="tv-score-fill" style={{ width: `${barWidth}%` }} />
        </div>
      </div>
    </div>
  );
};

const TopVendors = () => {
  const dispatch = useDispatch();

  const [vendors,      setVendors]      = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [search,       setSearch]       = useState("");
  const [searchInput,  setSearchInput]  = useState("");
  const [minRating,    setMinRating]    = useState(0);
  const [minCompleted, setMinCompleted] = useState(0);
  const [loading,      setLoading]      = useState(false);

  const limit = 12;

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    dispatch(loadingTrue());

    let url = `/top_vendors/top_vendor.php?page=${page}&limit=${limit}`;
    if (search)       url += `&q=${encodeURIComponent(search)}`;
    if (minRating)    url += `&min_rating=${minRating}`;
    if (minCompleted) url += `&min_completed=${minCompleted}`;

    const res = await Handler({ method: "GET", url });

    dispatch(loadingFalse());
    setLoading(false);

    if (res?.success) {
      setVendors(res.data?.list || []);
      setTotal(res.data?.pagination?.total || 0);
    }
  }, [page, search, minRating, minCompleted]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const handleSearch = () => { setPage(1); setSearch(searchInput); };
  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };
  const handleReset = () => {
    setSearchInput(""); setSearch("");
    setMinRating(0); setMinCompleted(0); setPage(1);
  };

  const totalPages = Math.ceil(total / limit);
  const startRank  = (page - 1) * limit;

  return (
    <MainLayout>
      <FeedLayout>
        <div className="tv-page">

          {/* Header */}
          <div className="tv-header">
            <div>
              <h1 className="tv-title">
                 Top Vendors
              </h1>
              <p className="tv-subtitle">Ranked by ratings, completed deals &amp; events</p>
            </div>
            <div className="tv-total-badge">{total} Vendors</div>
          </div>

          {/* Filters */}
          <div className="tv-filters">
            <div className="tv-search-bar">
              <i className="bx bx-search tv-search-icon" />
              <input
                className="tv-search-input"
                placeholder="Search vendor name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <button className="tv-btn-search" onClick={handleSearch}>Search</button>
            </div>

            <div className="tv-filter-row">
              <div className="tv-filter-group">
                <label className="tv-filter-label">Min Rating</label>
                <select
                  className="tv-select"
                  value={minRating}
                  onChange={(e) => { setMinRating(parseFloat(e.target.value)); setPage(1); }}
                >
                  <option value={0}>Any</option>
                  <option value={1}>1★ &amp; above</option>
                  <option value={2}>2★ &amp; above</option>
                  <option value={3}>3★ &amp; above</option>
                  <option value={4}>4★ &amp; above</option>
                  <option value={4.5}>4.5★ &amp; above</option>
                </select>
              </div>

              <div className="tv-filter-group">
                <label className="tv-filter-label">Min Completed</label>
                <select
                  className="tv-select"
                  value={minCompleted}
                  onChange={(e) => { setMinCompleted(parseInt(e.target.value)); setPage(1); }}
                >
                  <option value={0}>Any</option>
                  <option value={1}>1+</option>
                  <option value={3}>3+</option>
                  <option value={5}>5+</option>
                  <option value={10}>10+</option>
                </select>
              </div>

              {(search || minRating > 0 || minCompleted > 0) && (
                <button className="tv-btn-reset" onClick={handleReset}>
                  <i className="bx bx-x" /> Reset
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="tv-loading">
              <i className="bx bx-loader-alt bx-spin tv-loading-icon" />
              <p>Loading vendors...</p>
            </div>
          ) : vendors.length === 0 ? (
            <div className="tv-empty">
              <i className="bx bx-user-x tv-empty-icon" />
              <p>No vendors found</p>
              {(search || minRating > 0 || minCompleted > 0) && (
                <button className="ev-btn-secondary mt-2" onClick={handleReset}>
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="tv-grid">
              {vendors.map((v, i) => (
                <VendorCard key={v.vendor_id} vendor={v} rank={startRank + i + 1} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="tv-pagination">
              <button className="ev-btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                <i className="bx bx-chevron-left" /> Prev
              </button>
              <span className="tv-page-info">{page} / {totalPages}</span>
              <button className="ev-btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next <i className="bx bx-chevron-right" />
              </button>
            </div>
          )}

        </div>
      </FeedLayout>
    </MainLayout>
  );
};

export default TopVendors;